import 'swagger-ui-react/swagger-ui.css';
import React, { FunctionComponent, Suspense, useCallback, useEffect, useMemo, useRef } from 'react';
import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';
import {
  HTTP_HEADERS,
  buildCSRFRetryRequestConfig,
  transformFetchResponse,
  logTryItOutEvent,
} from '@modules/openCloud/swagger/components/tryItOut';
import { convertRobloxUrlToCurrentEnvironment } from '@modules/utilities/constants/baseUrl';
import {
  SwaggerUIRequest,
  SwaggerUIResponse,
  CachedRequestData,
} from '@modules/openCloud/swagger/components/tryItOut/types';
import TryItOutAuthMode from '@modules/openCloud/swagger/types/TryItOutAuthMode';
import DocsSiteEventType from '@modules/eventStream/enum/DocsSiteEventType';
import { getEndpointRiskLevel } from '@modules/openCloud/swagger/config/endpointRiskLevels';
import { useOpenAPIDocument } from '../hooks/useOpenAPIDocument';
import { DetailsSwaggerUIPlugin } from './DetailsSwaggerUIPlugin';
import useTryItOut from '../hooks/useTryItOut';

// Hardcoded to prevent swagger-ui-react's zensmooth library from causing issues
// https://github.com/swagger-api/swagger-ui/issues/5978
if (typeof window !== 'undefined') {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (window as any).noZensmooth = true;
}

const SwaggerUI = React.lazy(() => import('swagger-ui-react'));

export interface DetailsSwaggerUIProps {
  operation: OpenAPI.OperationObject;
  path: string;
  method: OpenAPI.HttpMethod;
}

const DetailsSwaggerUI: FunctionComponent<DetailsSwaggerUIProps> = ({
  operation,
  path,
  method,
}) => {
  const document = useOpenAPIDocument();
  const { authMode } = useTryItOut();
  // We have to use a ref here because Swagger UI does not capture requestInterceptor updates
  // The ref allows us to access the latest auth mode value in the requestInterceptor without a new reference
  const authModeRef = useRef(authMode);

  useEffect(() => {
    authModeRef.current = authMode;
  }, [authMode]);

  // Build minimal spec to interface with Swagger UI (single operation)
  const spec: OpenAPI.Document = useMemo(() => {
    const doc = {
      openapi: document.openapi,
      info: document.info,
      servers: document.servers,
      paths: {
        [path]: {
          [method.toLowerCase()]: operation,
        },
      },
      components: document.components,
    };

    return OpenAPI.removeUnusedComponents(doc);
  }, [document, operation, path, method]);

  const requestCache = useRef<Map<string, CachedRequestData>>(new Map());

  const requestInterceptor = useCallback((request: SwaggerUIRequest): SwaggerUIRequest => {
    const requestMethod = request.method.toUpperCase();

    request.credentials = authModeRef.current === TryItOutAuthMode.COOKIE ? 'include' : 'omit';

    const url = new URL(request.url);
    const riskLevel = getEndpointRiskLevel(requestMethod, url.origin, url.pathname);

    logTryItOutEvent(DocsSiteEventType.TryItOutClickExecute, {
      authMode: authModeRef.current,
      method: requestMethod,
      url: request.url,
      riskLevel,
    });

    request.url = convertRobloxUrlToCurrentEnvironment(request.url);

    // Store request info for CSRF handling using requestCache
    if (request.url) {
      requestCache.current.set(request.url, {
        method: request.method,
        body: request.body,
        credentials: request.credentials,
      });
    }

    return request;
  }, []);

  const responseInterceptor = useCallback(
    (response: SwaggerUIResponse): SwaggerUIResponse | Promise<SwaggerUIResponse> => {
      // Handle CSRF token retry logic for 403 responses
      if (response.status === 403 && response.headers) {
        const csrfToken = response.headers[HTTP_HEADERS.CSRF_TOKEN];

        if (csrfToken) {
          const cachedRequest = requestCache.current.get(response.url);
          const requestConfig = buildCSRFRetryRequestConfig(cachedRequest, csrfToken);

          return fetch(response.url, requestConfig)
            .then(transformFetchResponse)
            .catch(() => response);
        }
      }

      return response;
    },
    [],
  );

  return (
    <Suspense fallback={<div />}>
      <SwaggerUI
        requestInterceptor={requestInterceptor}
        responseInterceptor={responseInterceptor}
        spec={spec}
        defaultModelExpandDepth={4}
        defaultModelsExpandDepth={4}
        plugins={[DetailsSwaggerUIPlugin]}
        layout='DetailsSwaggerUILayout'
      />
    </Suspense>
  );
};

export default DetailsSwaggerUI;
